import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { SiteHeader } from '../components/SiteHeader'
import { ProfilePanel } from '../components/profile/ProfilePanel'
import { watchAuthState, resetPassword } from '../lib/firebase'
import { toast } from '../lib/toast'
import { useAgenticSourcing } from '../components/sourcing/useAgenticSourcing'

/**
 * /account/profile — the buyer's personal hub: what they've saved and how to
 * reach their own account. Deliberately NOT an edit surface — regular users
 * can't change their own or their team's identity (that's the org owner/admin
 * on /account/company, and company name/address needs Aska review). For now
 * the only self-service action is a password reset.
 *
 * Suppliers keep the operational editor (contacts, return policy, stock feeds)
 * until the role-separated company-editing flow ships — see the handoff note.
 */
export function AccountProfile() {
  const navigate = useNavigate()
  const [email, setEmail] = useState<string | null>(null)
  const [role, setRole] = useState<string | null>(null)
  // Profile → "My sourcing" tile — only when the agentic RFQ surface is armed.
  const sourcingOn = useAgenticSourcing()

  useEffect(() => {
    let unsub: (() => void) | undefined
    try {
      unsub = watchAuthState(async (user) => {
        setEmail(user?.email ?? null)
        if (!user) { setRole(null); return }
        try {
          const tok = await user.getIdTokenResult()
          const r = (tok.claims as { role?: string }).role
          setRole(r === 'admin' || r === 'supplier' ? r : 'buyer')
        } catch { setRole('buyer') }
      })
    } catch { /* signed-out */ }
    return () => unsub?.()
  }, [])

  // Suppliers keep their operational self-service until the company flow ships.
  if (role === 'supplier') {
    return (
      <>
        <SiteHeader />
        <main className="min-h-[60vh] pb-16">
          <ProfilePanel onBack={() => navigate('/search')} />
        </main>
      </>
    )
  }

  return (
    <>
      <SiteHeader />
      <main className="mx-auto min-h-[70vh] max-w-3xl px-6 py-12">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">Your account</p>
        <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight text-ink">Account</h1>
        {email && <p className="mt-1.5 text-sm text-soft">{email}</p>}

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <HubTile to="/favorites" title="Saved stones" desc="Diamonds you've bookmarked" />
          <HubTile to="/favorites?tab=suppliers" title="Saved suppliers" desc="Suppliers you've hearted" />
          <HubTile to="/shares" title="My shares" desc="Your marked-up share lists" />
          <HubTile to="/account/company" title="Company & team" desc="Your company details and members" />
          {sourcingOn && (
            <HubTile to="/account/sourcing" title="My sourcing" desc="Track your sourcing agent" />
          )}
        </div>

        <div className="mt-10 rounded-2xl border border-line bg-paper p-6">
          <h2 className="text-[15px] font-semibold text-ink">Password</h2>
          <p className="mt-1 text-[13px] text-soft">
            We'll email you a secure link to set a new password. Changing your company name,
            address, or team is handled with Aska — reach us at{' '}
            <a href="mailto:info@thediaex.com" className="text-primary hover:underline">info@thediaex.com</a>.
          </p>
          <ResetPasswordButton email={email} />
        </div>
      </main>
    </>
  )
}

function HubTile({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between rounded-2xl border border-line bg-paper p-5 no-underline transition-colors hover:border-gold/40"
    >
      <span className="min-w-0">
        <span className="block text-[15px] font-semibold text-ink">{title}</span>
        <span className="mt-0.5 block text-[12.5px] text-soft">{desc}</span>
      </span>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-soft transition-colors group-hover:text-gold">
        <path d="M9 18 15 12 9 6" />
      </svg>
    </Link>
  )
}

function ResetPasswordButton({ email }: { email: string | null }) {
  const [sending, setSending] = useState(false)
  async function onReset() {
    if (!email || sending) return
    setSending(true)
    try {
      await resetPassword(email)
      toast(`Password reset link sent to ${email}.`, 'success')
    } catch {
      toast('Could not send the reset link — try again.', 'error')
    } finally {
      setSending(false)
    }
  }
  return (
    <button
      type="button"
      onClick={onReset}
      disabled={!email || sending}
      className="mt-4 inline-flex h-10 items-center justify-center rounded-full bg-navy px-5 text-[13.5px] font-semibold text-white hover:bg-[#0a1a30] disabled:opacity-50 transition-colors"
      data-testid="reset-password"
    >
      {sending ? 'Sending…' : 'Send password reset link'}
    </button>
  )
}
